import styled from 'styled-components';
import { Menu, MenuButton, MenuItem } from '@szhsin/react-menu';

export const MobileOpenBtn = styled.button`
  display: none;

  @media (max-width: 1199px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 12px;
    left: 12px;
    z-index: 80;
    width: 40px;
    height: 40px;
    padding: 0;
    border: 1px solid var(--ink-line);
    border-radius: 12px;
    background: var(--ink-panel);
    box-shadow: var(--shadow-lg);
    cursor: pointer;

    img {
      display: block;
      width: 22px;
      height: 22px;
      object-fit: contain;
    }
  }
`;

export const SideBarBackdrop = styled.button`
  display: none;

  @media (max-width: 1199px) {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 69;
    border: 0;
    padding: 0;
    margin: 0;
    background: rgba(10, 9, 8, 0.55);
    cursor: pointer;
  }
`;

export const StyledSideBar = styled.div`
  background: var(--ink);
  padding: 0;
  position: sticky;
  top: 0;
  height: 100dvh;
  max-height: 100dvh;
  width: ${({ $sideBar }) => ($sideBar ? '260px' : '64px')};
  display: flex;
  flex-direction: column;
  color: var(--on-ink);
  z-index: 70;
  flex-shrink: 0;
  overflow: hidden;
  transition: width 0.2s var(--ease);
  border-right: 1px solid var(--ink-line);
  /* Ink chrome keeps light type on cream page */
  --sidebar-bg: var(--ink);
  --sidebar-text: #ffffff;
  --sidebar-muted: var(--on-ink-2);
  --sidebar-border: var(--ink-line);
  --headings-color: var(--sidebar-text);
  --text-main: var(--sidebar-text);
  --text-muted: var(--sidebar-muted);
  --base-text-color: var(--sidebar-text);

  @media (max-width: 1199px) {
    position: fixed;
    left: ${({ $mobileOpen }) => ($mobileOpen ? '0' : '-100%')};
    top: 0;
    bottom: 0;
    height: 100dvh;
    width: min(300px, 86vw);
    max-width: 300px;
    transition: left 0.28s var(--ease);
    box-shadow: ${({ $mobileOpen }) => ($mobileOpen ? 'var(--shadow-lg)' : 'none')};
  }

  @media (max-width: 480px) {
    width: min(280px, 88vw);
  }

  .scroll-area {
    display: flex;
    flex-direction: column;
    flex: 1 1 0;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--ink-line);
      border-radius: 4px;
    }

    &:hover::-webkit-scrollbar-thumb {
      background: rgba(245, 241, 234, 0.28);
    }
  }

  .nav-wrapper {
    width: 100%;
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .nav-header {
      display: flex;
      align-items: center;
      justify-content: ${({ $sideBar }) => ($sideBar ? 'space-between' : 'center')};
      gap: 8px;
      height: 64px;
      flex-shrink: 0;
      padding: ${({ $sideBar }) => ($sideBar ? '0 16px' : '0 8px')};
      border-bottom: 1px solid var(--ink-line);

      @media (max-width: 1199px) {
        justify-content: space-between;
        padding: 0 14px;
        height: 56px;
      }
    }

    .menu-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 32px;
      height: 32px;
      padding: 0;
      border: 0;
      border-radius: 8px;
      background: transparent;
      color: var(--on-ink);
      cursor: pointer;
      transition: background 0.2s var(--ease);

      &:hover {
        background: rgba(245, 241, 234, 0.08);
      }
    }

    .logo {
      width: 28px;
      flex-shrink: 0;
      overflow: hidden;
      transition: all ease-in-out 0.3s;

      @media (max-width: 1199px) {
        width: min(132px, 58vw);
      }

      .sidebar-active & {
        width: 148px;

        @media (max-width: 1199px) {
          width: min(132px, 58vw);
        }
      }

      a {
        display: flex;
        align-items: center;
      }

      img {
        max-width: 100%;
        height: auto;
        object-fit: contain;

        &.logo-lg {
          display: none;
          max-height: 32px;

          .sidebar-active & {
            display: block;
          }

          @media (max-width: 1199px) {
            display: block;
            max-height: 28px;
          }
        }

        &.logo-sm {
          display: block;
          width: 24px;
          height: auto;

          .sidebar-active & {
            display: none;
          }

          @media (max-width: 1199px) {
            display: none;
          }
        }
      }
    }

    .nav-links {
      padding: 12px 10px;
      font-size: 15px;
      line-height: 20px;
      list-style: none;
      margin: 0;
      flex-shrink: 0;

      li {
        position: relative;
        width: 100%;
        text-align: left;
        margin-bottom: 2px;
      }

      a,
      button {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 12px;
        overflow: hidden;
        transition:
          background 0.2s var(--ease),
          color 0.2s var(--ease),
          box-shadow 0.2s var(--ease);
        padding: 8px 10px;
        border-radius: 10px;
        position: relative;
        background: transparent;
        color: var(--on-ink);
        text-align: left;
        border: 0;
        cursor: pointer;
        font-weight: 500;

        @media (min-width: 1200px) {
          ${({ $sideBar }) =>
            !$sideBar &&
            `
            justify-content: center;
            padding: 8px;
            gap: 0;
          `}
        }

        &:hover:not(.active):not(:has(.active-bar)) {
          background: rgba(245, 241, 234, 0.06);
        }

        /* Sample person accent: orange + ink label (driven by existing active-bar) */
        &.active,
        &:has(.active-bar) {
          background: var(--orange);
          color: var(--ink);
          box-shadow: 0 0 0 1px var(--o-halo);

          .label,
          .icon {
            color: var(--ink);
          }

          img.icon {
            filter: brightness(0);
          }
        }

        .active-bar {
          position: absolute;
          width: 0;
          height: 0;
          overflow: hidden;
          opacity: 0;
          pointer-events: none;
        }

        .label {
          display: block;
          color: inherit;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;

          @media (min-width: 1200px) {
            ${({ $sideBar }) => !$sideBar && `display: none;`}
          }
        }
      }

      .icon {
        width: 22px;
        height: 22px;
        flex-shrink: 0;
        color: inherit;
      }
    }

    .chat-history {
      flex: 1 1 auto;
      min-height: 0;
      display: none;
      flex-direction: column;
      padding: 8px 12px 12px;
      border-width: 1px 0 0;
      border-style: solid;
      border-color: var(--ink-line);

      .sidebar-active & {
        display: flex;
      }

      @media (max-width: 1199px) {
        display: flex;
      }

      .head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 8px;
        flex-shrink: 0;
      }

      .sessions-wrap {
        width: 100%;
        min-width: 0;
      }
    }

    .subheading {
      display: block;
      font-size: 11px;
      line-height: 16px;
      font-weight: 600;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--on-ink-2);
      margin: 0;
    }
  }

  .sidebar-footer {
    flex-shrink: 0;
    margin-top: auto;
    display: flex;
    flex-direction: column;
    background: var(--ink);
  }

  .nav-links.nav2 {
    flex-shrink: 0;
    margin: 0;
    padding: 8px 10px 12px;
    list-style: none;
    border-top: 1px solid var(--ink-line);
    font-size: 15px;
    line-height: 20px;
    background: var(--ink);

    li {
      position: relative;
      width: 100%;
      text-align: left;
      margin-bottom: 2px;
    }

    a,
    button {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 12px;
      overflow: hidden;
      transition:
        background 0.2s var(--ease),
        color 0.2s var(--ease),
        box-shadow 0.2s var(--ease);
      padding: 8px 10px;
      border-radius: 10px;
      position: relative;
      background: transparent;
      color: var(--on-ink);
      text-align: left;
      border: 0;
      cursor: pointer;
      font-weight: 500;

      @media (min-width: 1200px) {
        ${({ $sideBar }) =>
          !$sideBar &&
          `
          justify-content: center;
          padding: 8px;
          gap: 0;
        `}
      }

      &:hover:not(.active):not(.btn-logout):not(:has(.active-bar)) {
        background: rgba(245, 241, 234, 0.06);
      }

      &.active,
      &:has(.active-bar) {
        background: var(--orange);
        color: var(--ink);
        box-shadow: 0 0 0 1px var(--o-halo);

        .label,
        .icon {
          color: var(--ink);
        }
      }

      .active-bar {
        position: absolute;
        width: 0;
        height: 0;
        overflow: hidden;
        opacity: 0;
        pointer-events: none;
      }

      .label {
        display: block;
        color: inherit;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        @media (min-width: 1200px) {
          ${({ $sideBar }) => !$sideBar && `display: none;`}
        }
      }
    }

    .icon {
      width: 22px;
      height: 22px;
      flex-shrink: 0;
      color: inherit;
    }

    .btn-logout {
      font-size: 15px;
      line-height: 20px;
      color: var(--on-ink-2);

      &:hover,
      &:focus {
        background: rgba(245, 241, 234, 0.08);
        color: var(--on-ink);
        box-shadow: none;
      }
    }
  }

  .profile-holder {
    cursor: pointer;
    position: relative;
    width: 100%;
    z-index: 3;
    overflow: hidden;
    padding: 12px 16px;
    border-top: 1px solid var(--ink-line);
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    flex-shrink: 0;
    transition: background 0.2s var(--ease);

    @media (min-width: 1200px) {
      ${({ $sideBar }) =>
        !$sideBar &&
        `
        padding: 12px 8px;
        justify-content: center;
      `}
    }

    &:hover {
      background: rgba(245, 241, 234, 0.04);
    }

    .icon-arrow {
      margin-left: auto;
      font-size: 18px;
      line-height: 0;
      opacity: 1;
      visibility: visible;
      color: var(--on-ink-2);

      @media (min-width: 1200px) {
        ${({ $sideBar }) =>
          !$sideBar &&
          `
          display: none;
        `}
      }
    }

    .profile-img {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      background: var(--orange);

      .text {
        display: block;
        font-size: 14px;
        line-height: 16px;
        text-transform: uppercase;
        font-weight: 600;
        color: var(--ink);
      }
    }

    .profile-info {
      flex-grow: 1;
      min-width: 0;
      color: var(--on-ink);
      text-transform: capitalize;

      @media (min-width: 1200px) {
        ${({ $sideBar }) => !$sideBar && `display: none;`}
      }

      .name {
        display: block;
        font-size: 14px;
        line-height: 18px;
        font-weight: 600;
        margin-bottom: 2px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .text {
        display: block;
        font-size: 12px;
        line-height: 16px;
        font-weight: 400;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--on-ink-2);
      }
    }
  }
`;

export const MenuBtn = styled(MenuButton)`
  max-width: 100px;
  overflow: hidden;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--on-ink-2);
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.15s var(--ease);

  &:hover,
  &:focus {
    color: var(--on-ink);
    background: transparent;
  }

  .agent-name {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ReponseMenu = styled(Menu)`
  box-shadow: var(--shadow-lg);

  ul {
    min-width: 120px;
    max-width: 180px;
    padding: 6px;
    border-radius: 12px;
    border: 1px solid var(--ink-line);
    background: var(--ink-panel);
    color: var(--on-ink);
    max-height: 250px;
    overflow-y: auto;

    .text {
      font-size: 13px;
      line-height: 17px;
      color: var(--on-ink);
    }
  }
  &.szh-menu-container {
    left: 0 !important;
    right: 0 !important;
  }
  .szh-menu {
    left: auto !important;
    right: 12px !important;
    z-index: 10 !important;
  }
`;

export const StyleItem = styled(MenuItem)`
  display: block;
  padding: 6px 8px;
  margin: 0 0 2px;
  border-radius: 8px;
  color: var(--on-ink);
  cursor: pointer;

  &.active,
  &:hover {
    background: rgba(245, 241, 234, 0.08);
  }

  &:last-child {
    margin: 0;
  }
`;
